/**
 * ViewportManager handles zoom, pan, and touch interactions for the canvas
 * Implements all viewport controls matching the current react-konva implementation
 */

import Konva from 'konva';
import { ViewportTransform, Point, TouchState, PanState } from './types';
import { EventEmitter } from './EventEmitter';

export interface ViewportManagerOptions {
  initialScale?: number;
  minScale?: number;
  maxScale?: number;
  zoomStep?: number;
  panSpeed?: number;
}

const DEFAULT_OPTIONS: Required<ViewportManagerOptions> = {
  initialScale: 0.5,
  minScale: 0.1,
  maxScale: 3,
  zoomStep: 1.2,
  panSpeed: 1.5,
};

export class ViewportManager {
  private stage: Konva.Stage;
  private layer: Konva.Layer;
  private emitter: EventEmitter;
  private options: Required<ViewportManagerOptions>;
  
  // Viewport state
  private scale: number;
  private position: Point;
  
  // Pan state
  private panState: PanState = {
    isPanning: false,
    startX: 0,
    startY: 0,
  };
  
  // Touch state for pinch-to-zoom
  private touchState: TouchState = {
    lastCenter: null,
    lastDist: 0,
  };
  
  // Panning disabled flag (for edit mode)
  private panningDisabled: boolean = false;
  
  // Container for fullscreen
  private container: HTMLDivElement;

  constructor(
    stage: Konva.Stage,
    layer: Konva.Layer,
    container: HTMLDivElement,
    emitter: EventEmitter,
    options: ViewportManagerOptions = {}
  ) {
    this.stage = stage;
    this.layer = layer;
    this.container = container;
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    
    this.scale = this.options.initialScale;
    this.position = { x: 0, y: 0 };
    
    this.setupEventListeners();
    this.applyTransform();
  }

  /**
   * Setup all event listeners for viewport interactions
   */
  private setupEventListeners(): void {
    // Mouse wheel for zoom/pan
    this.stage.on('wheel', this.handleWheel.bind(this));
    
    // Mouse events for panning
    this.stage.on('mousedown', this.handleMouseDown.bind(this));
    this.stage.on('mousemove', this.handleMouseMove.bind(this));
    this.stage.on('mouseup', this.handleMouseUp.bind(this));
    this.stage.on('mouseleave', this.handleMouseUp.bind(this));
    
    // Touch events for mobile support
    this.stage.on('touchstart', this.handleTouchStart.bind(this));
    this.stage.on('touchmove', this.handleTouchMove.bind(this));
    this.stage.on('touchend', this.handleTouchEnd.bind(this));
  }

  /**
   * Handle mouse wheel for zoom (with Ctrl) or pan (without Ctrl)
   * Also handles trackpad pinch gestures (which often send ctrlKey automatically)
   */
  private handleWheel(e: Konva.KonvaEventObject<WheelEvent>): void {
    e.evt.preventDefault();
    
    const pointer = this.stage.getPointerPosition();
    if (!pointer) return;
    
    // Check if Ctrl/Cmd is pressed for zoom (or trackpad pinch gesture)
    // Trackpads often send ctrlKey automatically for pinch gestures
    const isZoomGesture = e.evt.ctrlKey || e.evt.metaKey;
    
    if (isZoomGesture) {
      // Zoom in/out at pointer position
      // Trackpad pinch gestures typically have smaller deltaY values
      const zoomIn = e.evt.deltaY < 0;
      this.zoomAtPoint(pointer, zoomIn);
    } else {
      // Pan vertically without Ctrl (trackpad two-finger scroll)
      const deltaY = e.evt.deltaY * this.options.panSpeed;
      this.position = {
        x: this.position.x,
        y: this.position.y - deltaY,
      };
      this.applyTransform();
    }
  }

  /**
   * Zoom in or out centered on a point
   */
  private zoomAtPoint(pointer: Point, zoomIn: boolean): void {
    const oldScale = this.scale;
    
    const mousePointTo = {
      x: (pointer.x - this.position.x) / oldScale,
      y: (pointer.y - this.position.y) / oldScale,
    };
    
    const factor = zoomIn ? 1.1 : 1 / 1.1;
    const newScale = this.clampScale(oldScale * factor);
    
    this.scale = newScale;
    this.position = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    
    this.applyTransform();
  }

  /**
   * Handle mouse down for panning
   */
  private handleMouseDown(e: Konva.KonvaEventObject<MouseEvent>): void {
    // Middle mouse button (1) always pans, regardless of panningDisabled
    const isMiddleButton = e.evt.button === 1;
    
    // If panning is disabled for left/right click, only allow middle mouse button
    if (this.panningDisabled && !isMiddleButton) return;
    
    // Only pan when clicking on empty area (stage, layer, or background)
    const target = e.target;
    const isEmptyArea = target === this.stage || 
      target.getClassName() === 'Layer' ||
      (target.attrs && target.attrs.fillPatternRepeat === 'repeat');
    
    if (!isEmptyArea && !isMiddleButton) return;
    
    // Check for left mouse button (0), middle mouse button (1), or Shift key
    if (e.evt.button === 0 || e.evt.button === 1 || e.evt.shiftKey) {
      e.evt.preventDefault();
      
      const pos = this.stage.getPointerPosition();
      if (pos) {
        this.panState = {
          isPanning: true,
          startX: pos.x - this.position.x,
          startY: pos.y - this.position.y,
        };
      }
    }
  }

  /**
   * Handle mouse move for panning
   */
  private handleMouseMove(e: Konva.KonvaEventObject<MouseEvent>): void {
    if (!this.panState.isPanning) return;
    
    const pos = this.stage.getPointerPosition();
    if (pos) {
      this.position = {
        x: pos.x - this.panState.startX,
        y: pos.y - this.panState.startY,
      };
      this.applyTransform();
    }
  }

  /**
   * Handle mouse up to stop panning
   */
  private handleMouseUp(): void {
    this.panState.isPanning = false;
  }

  /**
   * Handle touch start for pinch-to-zoom and pan
   */
  private handleTouchStart(e: Konva.KonvaEventObject<TouchEvent>): void {
    const touch1 = e.evt.touches[0];
    const touch2 = e.evt.touches[1];
    
    if (touch1 && touch2) {
      // Two fingers - prepare for pinch zoom
      this.touchState.lastCenter = null;
      this.touchState.lastDist = 0;
    } else if (touch1) {
      // One finger - prepare for pan
      this.panState = {
        isPanning: true,
        startX: touch1.clientX - this.position.x,
        startY: touch1.clientY - this.position.y,
      };
    }
  }

  /**
   * Handle touch move for pinch-to-zoom and pan
   */
  private handleTouchMove(e: Konva.KonvaEventObject<TouchEvent>): void {
    e.evt.preventDefault();
    
    const touch1 = e.evt.touches[0];
    const touch2 = e.evt.touches[1];
    
    if (touch1 && touch2) {
      // Two fingers - pinch to zoom
      const p1 = { x: touch1.clientX, y: touch1.clientY };
      const p2 = { x: touch2.clientX, y: touch2.clientY };
      const newCenter = this.getCenter(p1, p2);
      const dist = this.getDistance(p1, p2);
      
      if (!this.touchState.lastCenter) {
        this.touchState.lastCenter = newCenter;
        this.touchState.lastDist = dist;
        return;
      }
      
      // Calculate zoom based on distance change
      const pointTo = {
        x: (newCenter.x - this.position.x) / this.scale,
        y: (newCenter.y - this.position.y) / this.scale,
      };
      
      const newScale = this.clampScale(this.scale * (dist / this.touchState.lastDist));
      
      this.scale = newScale;
      this.position = {
        x: newCenter.x - pointTo.x * newScale,
        y: newCenter.y - pointTo.y * newScale,
      };
      
      this.touchState.lastCenter = newCenter;
      this.touchState.lastDist = dist;
      
      this.applyTransform();
    } else if (touch1 && this.panState.isPanning) {
      // One finger - pan
      this.position = {
        x: touch1.clientX - this.panState.startX,
        y: touch1.clientY - this.panState.startY,
      };
      this.applyTransform();
    }
  }

  /**
   * Handle touch end
   */
  private handleTouchEnd(): void {
    this.panState.isPanning = false;
    this.touchState.lastCenter = null;
    this.touchState.lastDist = 0;
  }

  /**
   * Get distance between two points
   */
  private getDistance(p1: Point, p2: Point): number {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  /**
   * Get center point between two points
   */
  private getCenter(p1: Point, p2: Point): Point {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  }

  /**
   * Clamp scale to valid range
   */
  private clampScale(scale: number): number {
    return Math.max(this.options.minScale, Math.min(this.options.maxScale, scale));
  }

  /**
   * Apply the current transform to the layer
   */
  private applyTransform(): void {
    this.layer.scaleX(this.scale);
    this.layer.scaleY(this.scale);
    this.layer.x(this.position.x);
    this.layer.y(this.position.y);
    this.layer.batchDraw();
    
    this.emitter.emit('transformChange', {
      transform: this.getTransform(),
    });
  }

  /**
   * Get current transform state
   */
  getTransform(): ViewportTransform {
    return {
      scale: this.scale,
      x: this.position.x,
      y: this.position.y,
    };
  }

  /**
   * Set transform state
   */
  setTransform(transform: Partial<ViewportTransform>): void {
    if (transform.scale !== undefined) {
      this.scale = this.clampScale(transform.scale);
    }
    if (transform.x !== undefined) {
      this.position.x = transform.x;
    }
    if (transform.y !== undefined) {
      this.position.y = transform.y;
    }
    this.applyTransform();
  }

  /**
   * Zoom in from center
   */
  zoomIn(): void {
    const centerX = this.stage.width() / 2;
    const centerY = this.stage.height() / 2;
    
    const oldScale = this.scale;
    const newScale = this.clampScale(this.scale * this.options.zoomStep);
    
    const mousePointTo = {
      x: (centerX - this.position.x) / oldScale,
      y: (centerY - this.position.y) / oldScale,
    };
    
    this.scale = newScale;
    this.position = {
      x: centerX - mousePointTo.x * newScale,
      y: centerY - mousePointTo.y * newScale,
    };
    
    this.applyTransform();
  }

  /**
   * Zoom out from center
   */
  zoomOut(): void {
    const centerX = this.stage.width() / 2;
    const centerY = this.stage.height() / 2;
    
    const oldScale = this.scale;
    const newScale = this.clampScale(this.scale / this.options.zoomStep);
    
    const mousePointTo = {
      x: (centerX - this.position.x) / oldScale,
      y: (centerY - this.position.y) / oldScale,
    };
    
    this.scale = newScale;
    this.position = {
      x: centerX - mousePointTo.x * newScale,
      y: centerY - mousePointTo.y * newScale,
    };
    
    this.applyTransform();
  }

  /**
   * Toggle fullscreen mode
   */
  fullscreen(): void {
    if (!document.fullscreenElement) {
      this.container.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }

  /**
   * Fit content into view
   */
  fitToContent(): void {
    this.layer.batchDraw();
    
    // Get all children except background
    const allChildren = this.layer.getChildren();
    const contentChildren = allChildren.filter((node) => {
      // Exclude the dotted background pattern
      return !(node.className === 'Rect' && node.attrs.fillPatternImage !== undefined);
    });
    
    if (contentChildren.length === 0) return;
    
    // Calculate bounds
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasValidBounds = false;
    
    contentChildren.forEach((child) => {
      try {
        const box = child.getClientRect({ skipTransform: false });
        if (box && box.width > 0 && box.height > 0) {
          minX = Math.min(minX, box.x);
          minY = Math.min(minY, box.y);
          maxX = Math.max(maxX, box.x + box.width);
          maxY = Math.max(maxY, box.y + box.height);
          hasValidBounds = true;
        }
      } catch (e) {
        // Skip nodes that can't provide bounds
      }
    });
    
    if (!hasValidBounds || !isFinite(minX) || !isFinite(minY)) return;
    
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    
    if (contentWidth <= 0 || contentHeight <= 0) return;
    
    const contentCenterX = minX + contentWidth / 2;
    const contentCenterY = minY + contentHeight / 2;
    
    const stageWidth = this.stage.width();
    const stageHeight = this.stage.height();
    
    // Calculate scale to fit content with padding
    const padding = 100;
    const scaleX = (stageWidth - padding * 2) / contentWidth;
    const scaleY = (stageHeight - padding * 2) / contentHeight;
    const newScale = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 100%
    
    // Calculate position to center content
    const newX = stageWidth / 2 - contentCenterX * newScale;
    const newY = stageHeight / 2 - contentCenterY * newScale;
    
    this.scale = newScale;
    this.position = { x: newX, y: newY };
    this.applyTransform();
  }

  /**
   * Enable or disable panning (for edit mode)
   */
  setPanningDisabled(disabled: boolean): void {
    this.panningDisabled = disabled;
  }

  /**
   * Check if currently panning
   */
  isPanning(): boolean {
    return this.panState.isPanning;
  }

  /**
   * Get cursor style based on current state
   */
  getCursor(): string {
    return this.panState.isPanning ? 'grabbing' : 'default';
  }

  /**
   * Convert screen coordinates to layer coordinates
   */
  screenToLayer(screenPoint: Point): Point {
    const transform = this.layer.getAbsoluteTransform().copy();
    transform.invert();
    return transform.point(screenPoint);
  }

  /**
   * Convert layer coordinates to screen coordinates
   */
  layerToScreen(layerPoint: Point): Point {
    const transform = this.layer.getAbsoluteTransform().copy();
    return transform.point(layerPoint);
  }

  /**
   * Get zoom controls for React components
   */
  getZoomControls() {
    return {
      scale: this.scale,
      zoomIn: this.zoomIn.bind(this),
      zoomOut: this.zoomOut.bind(this),
      fullscreen: this.fullscreen.bind(this),
    };
  }

  /**
   * Cleanup event listeners
   */
  destroy(): void {
    this.stage.off('wheel');
    this.stage.off('mousedown');
    this.stage.off('mousemove');
    this.stage.off('mouseup');
    this.stage.off('mouseleave');
    this.stage.off('touchstart');
    this.stage.off('touchmove');
    this.stage.off('touchend');
  }
}
